import { Anim } from '../../Animation/AnimationHelper';
import { Easing } from '@tweenjs/tween.js';
import TransformComponent from '../../../Component/Transform';
import Game from '../../../Game';
import { TickerEvent } from '../../../Utility/Ticker';
import Signal from '../../../Utility/Signal';
import CleanupManager from '../../../Utility/CleanupManager';

type WheelRotateAnimationProps = {
    wheel: GameObject;
    pointer: GameObject;
    sectorsAmount: number;
    prizeIndex: number;
    side: -1 | 1;
    speed: number;
    cleanupManager: CleanupManager;
}

export class WheelRotateAnimation {
    private readonly wheel: GameObject;
    private readonly pointer: GameObject;
    private readonly speed: number;
    private readonly sectorsAmount: number;
    private readonly prizeIndex: number;
    private readonly side: number;
    private currentSide: number;
    private updateFn: (dt: number) => void = (): void => void 0;
    public onComplete: Signal = new Signal<[]>();
    public onLapLeft: Signal = new Signal<[]>();
    public onConstSpeedStart: Signal = new Signal<[]>();

    constructor(props: WheelRotateAnimationProps) {
        this.wheel = props.wheel;
        this.pointer = props.pointer;
        this.sectorsAmount = props.sectorsAmount;
        this.prizeIndex = props.prizeIndex;
        this.side = props.side;
        this.speed = props.speed;
        this.currentSide = this.side;

        Game.ticker.on(TickerEvent.TICK, this.update);

        props.cleanupManager.add(() => {
            Game.ticker.off(TickerEvent.TICK, this.update);
        });
    }

    private startBackIn = (): void => {
        this.currentSide = this.side * -1;

        Anim(this.wheel, { angle: this.angle - 10 * this.side, duration: 300, easing: Easing.Sinusoidal.InOut })
            .then(() => {
                this.currentSide = this.side;
                this.startIn();
            });

        this.updateFn = () => {
        };
    };

    private startIn = (): void => {
        let speed = 0;
        const acc = 0.2;

        this.updateFn = (dt: number) => {
            speed += acc;

            if (speed >= this.speed) {
                speed = this.speed;
                this.startConstSpeed();
            }

            this.angle += speed * this.side * dt;
        };
    };

    private startConstSpeed = (): void => {
        this.onConstSpeedStart.dispatch();

        this.updateFn = (dt: number) => {
            this.angle += this.speed * this.side * dt;
        };
    };

    private startOut = (): void => {
        const backOutAngle = 10;
        const lap = 360;
        let targetAngle = Math.ceil(Math.abs(this.angle) / lap) * lap;
        targetAngle += lap / this.sectorsAmount * this.prizeIndex * -this.side + backOutAngle;
        targetAngle *= this.side;

        this.updateFn = (dt: number) => {
            this.angle += this.speed * this.side * dt;

            if ((targetAngle - this.angle) * this.side < 0) {
                this.onLapLeft.dispatch();
                targetAngle += lap * 2 * this.side;

                const startAngle = this.angle;
                const range = Math.abs(targetAngle - startAngle);
                const minSpeed = 0.01;

                this.updateFn = (dt: number) => {
                    const p = Math.abs(this.angle - startAngle) / range;
                    const speed = Math.max(minSpeed, this.speed * (1 - p));
                    this.angle += speed * this.side * dt;

                    if ((targetAngle - this.angle) * this.side < backOutAngle * 0.5) {
                        this.startBackOut(targetAngle - backOutAngle * this.side);
                    }
                };
            }
        };
    };

    private startBackOut = (targetAngle: number): void => {
        this.currentSide = this.side * -1;

        Anim(this.wheel, { angle: targetAngle, duration: 1000, easing: Easing.Sinusoidal.InOut })
            .then(() => {
                this.currentSide = 1;
                this.onComplete.dispatch();
            });

        this.updateFn = () => {
        };
    };

    private update = (): void => {
        if (this.wheel.destroyed) return;

        const prevAngle = this.angle;
        this.updateFn(1);
        const speed = prevAngle - this.angle;

        const pointerTransform = this.pointer.getComponent(TransformComponent);
        const sectorAngle = 360 / this.sectorsAmount;
        let sectorPercent = (Math.abs(this.angle) + sectorAngle / 2) % sectorAngle / sectorAngle;
        let p = 0;
        sectorPercent = this.currentSide === 1 ? sectorPercent : 1 - sectorPercent;

        if (sectorPercent > 0.86) {
            p = 1;
        } else if (sectorPercent < 0.08) {
            p = 1 - sectorPercent / 0.08;
        }

        p = Math.min(1, p + Math.abs(speed / this.speed));
        const targetRotation = p * 12 / 180 * Math.PI * -this.currentSide;
        pointerTransform.rotation += (targetRotation - pointerTransform.rotation) * 0.3;
    };

    private set angle(v: number) {
        this.wheel.getComponent(TransformComponent).rotation = v / 180 * Math.PI;
    }

    private get angle(): number {
        return this.wheel.getComponent(TransformComponent).rotation / Math.PI * 180;
    }

    public start(): void {
        this.startBackIn();
    }

    public stop(): void {
        this.startOut();
    }
}
